<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <SellProductFiltersComponent
                    ref="sell-product-search"
                    :showShops="true"
                    :enableFieldType="false"
                    :showSingleArticleFilter="false"
                    preselectFamily
                    @searchPressed="getRESAAnalysis"
                    @changeFieldType="
                        (f) => {
                            fieldType = f;
                        }
                    "
                ></SellProductFiltersComponent>
            </v-col>
        </v-row>

        <!-- RESA Summary Cards -->
        <v-row dense v-if="!loading && summary">
            <v-col cols="12" md="8">
                <v-card>
                    <v-card-title class="text-h6">
                        Analisi RESA - Efficienza Spazio (Revenue per cm²)
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="4" v-for="(stats, classType) in summary.resa_distribution" :key="classType">
                                <div class="text-center">
                                    <v-chip large :color="getRESAColor(classType)" dark class="ma-2">
                                        Classe {{ classType }}
                                    </v-chip>
                                    <div class="mt-2">
                                        <div><strong>{{ stats.count }}</strong> prodotti</div>
                                        <div><small>{{ stats.percentage.toFixed(1) }}% del totale</small></div>
                                        <div class="mt-1">
                                            <strong>Fatturato:</strong> {{ stats.total_revenue | formatEuro }}
                                        </div>
                                        <div>
                                            <strong>Spazio:</strong> {{ stats.total_space | roundSecondDecimal }} cm²
                                        </div>
                                        <div>
                                            <strong>Efficienza:</strong> {{ stats.space_efficiency | formatEuro }}/cm²
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="4">
                <v-card>
                    <v-card-title class="text-h6">
                        Indicatori Spaziali
                    </v-card-title>
                    <v-card-text>
                        <div><strong>Totale prodotti:</strong> {{ summary.total_products }}</div>
                        <div><strong>Fatturato totale:</strong> {{ summary.space_analysis.total_revenue | formatEuro }}</div>
                        <div><strong>Spazio totale:</strong> {{ summary.space_analysis.total_space | roundSecondDecimal }} cm²</div>
                        <div><strong>RESA complessiva:</strong> {{ summary.space_analysis.overall_resa | formatEuro }}/cm²</div>
                        <div><strong>Dimensione media:</strong> {{ summary.space_analysis.avg_product_size | roundSecondDecimal }} cm²</div>
                        <div class="mt-2">
                            <v-chip small color="success" dark>
                                <strong>{{ summary.space_analysis.space_efficiency_leaders }}</strong> prodotti ad alta RESA
                            </v-chip>
                        </div>
                        <div class="mt-1">
                            <v-chip small color="error" dark>
                                <strong>{{ summary.space_analysis.space_efficiency_underperformers }}</strong> prodotti a bassa RESA
                            </v-chip>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Space Efficiency Chart Info -->
        <v-row dense v-if="!loading && summary">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="text-h6">
                        Guida all'Interpretazione RESA
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-chip color="success" dark class="ma-1">Classe A - Alta RESA</v-chip>
                                <p class="mt-2">Prodotti che generano il maggior fatturato per unità di spazio occupato. Massima efficienza spaziale.</p>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-chip color="warning" dark class="ma-1">Classe B - Media RESA</v-chip>
                                <p class="mt-2">Prodotti con efficienza spaziale moderata. Potrebbero beneficiare di ottimizzazioni.</p>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-chip color="error" dark class="ma-1">Classe C - Bassa RESA</v-chip>
                                <p class="mt-2">Prodotti che occupano spazio sproporzionato rispetto al fatturato. Candidati per riposizionamento.</p>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Data Table -->
        <v-row dense>
            <v-col>
                <div v-if="!loading">
                    <v-data-table
                        dense
                        multi-sort
                        fixed-header
                        :search="search"
                        :item-class="rowColorClass"
                        :height="600"
                        :headers="headers"
                        :items="filteredData"
                        :options="{
                            sortBy: ['resa_class', 'euro_cm2'],
                            sortDesc: [false, true]
                        }"
                        :items-per-page="100"
                        :footer-props="{
                            'items-per-page-options': [20, 50, 100, 200, -1],
                        }"
                    >
                        <template v-slot:top>
                            <v-toolbar flat>
                                <v-text-field
                                    dense
                                    v-model="search"
                                    label="Cerca..."
                                    class="mx-2"
                                    style="max-width: 200px;"
                                >
                                </v-text-field>
                                <v-select
                                    dense
                                    v-model="resaFilter"
                                    :items="resaFilterOptions"
                                    label="Filtro RESA"
                                    class="mx-2"
                                    style="max-width: 150px;"
                                    @change="applyFilters"
                                >
                                </v-select>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="success"
                                    small
                                    dark
                                    class="mb-2"
                                    @click="exportRESAAnalysis()"
                                >
                                    Esporta Analisi RESA
                                    <v-icon small>assessment</v-icon>
                                </v-btn>
                            </v-toolbar>
                        </template>
                        <template v-slot:item.cod_articolo="{ item }">
                            <router-link
                                target="_blank"
                                :to="{
                                    name: 'product',
                                    query: {
                                        id: item.cod_articolo,
                                    },
                                }"
                            >
                                {{ item.cod_articolo }}
                            </router-link>
                        </template>
                        <template v-slot:item.resa_class="{ item }">
                            <v-chip small :color="getRESAColor(item.resa_class)" dark>
                                {{ item.resa_class }}
                            </v-chip>
                        </template>
                        <template v-slot:item.prezzo_vendita="{ item }">
                            {{ item.prezzo_vendita | formatEuro }}
                        </template>
                        <template v-slot:item.vendite_totali="{ item }">
                            {{ item.vendite_totali | formatEuro }}
                        </template>
                        <template v-slot:item.euro_cm2="{ item }">
                            {{ item.euro_cm2 | formatEuro }}
                        </template>
                        <template v-slot:item.spazio="{ item }">
                            {{ item.spazio | roundSecondDecimal }}
                        </template>
                        <template v-slot:item.altezza="{ item }">
                            {{ item.altezza | roundSecondDecimal }}
                        </template>
                        <template v-slot:item.larghezza="{ item }">
                            {{ item.larghezza | roundSecondDecimal }}
                        </template>
                        <template v-slot:item.velocita="{ item }">
                            {{ item.velocita | roundSecondDecimal }}
                        </template>
                        <template v-slot:item.saturazione="{ item }">
                            {{ item.saturazione | formatPercent }}
                        </template>
                    </v-data-table>
                </div>
                <div v-else style="text-align: center">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                    ></v-progress-circular>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped lang="scss"></style>

<script>
import requests from "../../requests";
import SellProductFiltersComponent from "../../components/SellProductFiltersComponent.vue";
import $ from "jquery";

const headers = [
    { text: "Codice Articolo", value: "cod_articolo" },
    { text: "Descrizione", value: "ar_descr", width: 250 },
    { text: "RESA", value: "resa_class", align: "center", filterable: false },
    { text: "Euro/cm²", value: "euro_cm2", align: "end", filterable: false },
    { text: "Spazio(cm²)", value: "spazio", align: "end", filterable: false },
    { text: "Vendite Totali", value: "vendite_totali", align: "end", filterable: false },
    { text: "Quantità", value: "qty_veduta", align: "end", filterable: false },
    { text: "H(cm)", value: "altezza", align: "end" },
    { text: "W(cm)", value: "larghezza", align: "end" },
    { text: "Prezzo Vendita", value: "prezzo_vendita", align: "end", filterable: false },
    { text: "Velocità(pz/gg)", value: "velocita", align: "end", filterable: false },
    { text: "Saturazione", value: "saturazione", align: "end", filterable: false },
    { text: "Famiglia", value: "famiglia", width: 200 },
    { text: "Sottofamiglia", value: "sottofamiglia", width: 200 },
    { text: "Ultima Vendita", value: "data_ultima_vendita", filterable: false },
];

export default {
    name: "product-resa-analysis",
    components: { SellProductFiltersComponent },
    computed: {
        headers() {
            return headers;
        },
    },
    data: () => {
        return {
            loading: false,
            data: [],
            search: "",
            fieldType: "prezzo",
            resaFilter: "all",
            resaFilterOptions: [
                { text: "Tutti", value: "all" },
                { text: "A (Alta Efficienza Spaziale)", value: "A" },
                { text: "B (Media Efficienza Spaziale)", value: "B" },
                { text: "C (Bassa Efficienza Spaziale)", value: "C" }
            ],
            filteredData: [],
            summary: null,
        };
    },

    mounted() {
        console.log("Product RESA Analysis Mounted.");
        let filters = {};
        this.getRESAAnalysis(filters);
    },

    methods: {
        getRESAAnalysis(filters) {
            console.log(filters);
            this.loading = true;
            requests
                .getProductsRESA(filters)
                .then((res) => {
                    this.data = res.data.products || [];
                    this.summary = res.data.summary || null;
                    this.applyFilters();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        rowColorClass(row) {
            switch (row.resa_class) {
                case 'A': return "row-color-excellent";
                case 'B': return "row-color-very-good";
                case 'C': return "row-color-poor";
                default: return "";
            }
        },
        getRESAColor(resaClass) {
            switch (resaClass) {
                case 'A': return 'success';
                case 'B': return 'warning';
                case 'C': return 'error';
                default: return 'grey';
            }
        },
        exportRESAAnalysis() {
            let filters = this.$refs["sell-product-search"].getFilters();
            if (!filters.shop_id) {
                filters.shop_id = 7;
            }
            window.open(
                "/api/products/resa?" + $.param(filters),
                "_blank"
            );
        },
        applyFilters() {
            let filtered = this.data;

            // Apply RESA filter
            if (this.resaFilter !== "all") {
                filtered = filtered.filter(item => item.resa_class === this.resaFilter);
            }

            this.filteredData = filtered;
        }
    },

    filters: {
        roundSecondDecimal(value) {
            return Math.round(value * 100) / 100;
        },

        formatEuro(number) {
            if (_.isNaN(number)) return 0;
            return new Intl.NumberFormat("it-IT", {
                style: "currency",
                currency: "EUR",
            }).format(number);
        },
        formatPercent(value) {
            return value.toFixed(2) + "%";
        },
    },
};
</script>

<style lang="scss">
.row-color-excellent {
    background-color: rgba($color: #4CAF50, $alpha: 0.3) !important;
}
.row-color-very-good {
    background-color: rgba($color: #8BC34A, $alpha: 0.3) !important;
}
.row-color-poor {
    background-color: rgba($color: #F44336, $alpha: 0.3) !important;
}
</style>